import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Link as LinkIcon, Plus, Trash2, Loader2, ShieldAlert, ExternalLink } from "lucide-react";

const PLATFORMS = [
  ["website", "Website"], ["instagram", "Instagram"], ["facebook", "Facebook"],
  ["x", "X (Twitter)"], ["threads", "Threads"], ["tiktok", "TikTok"],
  ["youtube", "YouTube"], ["twitch", "Twitch"], ["linkedin", "LinkedIn"],
  ["github", "GitHub"], ["mastodon", "Mastodon"], ["bluesky", "Bluesky"],
  ["pinterest", "Pinterest"], ["reddit", "Reddit"], ["other", "Other"],
];

/**
 * Read-only badge list rendered on public profiles. External + unverified —
 * users MUST click through to visit; every link opens with noopener/noreferrer
 * so the target page can't touch our origin.
 */
export function SocialLinksList({ links }) {
  const { t } = useI18n();
  if (!links || links.length === 0) return null;
  return (
    <div className="mt-4" data-testid="profile-social-links">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
        {t("social_links_title")}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {links.map((l, i) => (
          <a
            key={i}
            href={l.url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-300 text-xs text-slate-700"
            data-testid={`social-link-${i}`}
          >
            <ExternalLink size={11} />
            <span className="font-semibold">{(l.label || l.platform).slice(0, 30)}</span>
            <span className="text-slate-400 hidden sm:inline">· {new URL(l.url).hostname}</span>
          </a>
        ))}
      </div>
      <div className="text-[10px] text-slate-500 italic mt-1">
        {t("social_links_disclaimer")}
      </div>
    </div>
  );
}

/**
 * Editor shown to the profile owner only. 18+ gate is enforced both here
 * (banner + disabled inputs) and on the backend via PATCH /auth/me.
 */
export default function SocialLinksEditor({ initial = [], onSaved, isAdult }) {
  const { t } = useI18n();
  const [links, setLinks] = useState(() => (initial || []).map((l) => ({ ...l })));
  const [busy, setBusy] = useState(false);

  const canEdit = !!isAdult;

  const addRow = () => {
    if (links.length >= 10) return;
    setLinks([...links, { platform: "website", url: "", label: "" }]);
  };
  const updateRow = (i, patch) => {
    setLinks(links.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  };
  const removeRow = (i) => setLinks(links.filter((_, idx) => idx !== i));

  const canSave = useMemo(() => {
    if (!canEdit) return false;
    for (const l of links) {
      if (!l.url) continue;
      if (!/^https?:\/\//i.test(l.url)) return false;
    }
    return true;
  }, [links, canEdit]);

  const save = async () => {
    setBusy(true);
    try {
      const cleaned = links
        .map((l) => ({ platform: l.platform || "other", url: (l.url || "").trim(), label: (l.label || "").trim() || undefined }))
        .filter((l) => l.url);
      const { data } = await api.patch("/auth/me", { social_links: cleaned });
      toast.success(t("social_links_saved"));
      onSaved && onSaved(data);
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      const map = {
        social_links_url_scheme: t("social_links_url_scheme_err"),
        social_links_url_invalid: t("social_links_url_invalid_err"),
        social_links_url_too_long: t("social_links_url_too_long_err"),
        social_links_adult_only: t("social_links_adult_only"),
      };
      toast.error(map[detail] || t("social_links_url_invalid_err"));
    } finally { setBusy(false); }
  };

  if (!canEdit) {
    return (
      <div className="rounded-xl border-2 border-amber-300 bg-amber-50 text-amber-900 p-3 flex items-center gap-2 text-sm" data-testid="social-links-adult-only">
        <ShieldAlert size={14} /> {t("social_links_adult_only")}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="social-links-editor">
      <div>
        <div className="font-semibold text-slate-900 flex items-center gap-2">
          <LinkIcon size={14} /> {t("social_links_title")}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">{t("social_links_desc")}</div>
      </div>

      {links.length === 0 && (
        <div className="text-xs text-slate-400 italic" data-testid="social-links-empty">{t("social_links_none")}</div>
      )}

      {links.map((l, i) => (
        <div key={i} className="grid grid-cols-1 sm:grid-cols-[140px_1fr_140px_auto] gap-2 items-start" data-testid={`social-link-row-${i}`}>
          <Select value={l.platform} onValueChange={(v) => updateRow(i, { platform: v })}>
            <SelectTrigger data-testid={`social-link-platform-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {PLATFORMS.map(([k, label]) => (<SelectItem key={k} value={k}>{label}</SelectItem>))}
            </SelectContent>
          </Select>
          <Input
            data-testid={`social-link-url-${i}`}
            value={l.url}
            onChange={(e) => updateRow(i, { url: e.target.value })}
            placeholder="https://example.com/…"
            maxLength={500}
          />
          <Input
            data-testid={`social-link-label-${i}`}
            value={l.label || ""}
            onChange={(e) => updateRow(i, { label: e.target.value })}
            placeholder={t("social_links_label")}
            maxLength={60}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => removeRow(i)}
            data-testid={`social-link-remove-${i}`}
            className="rounded-full"
          >
            <Trash2 size={13} />
          </Button>
        </div>
      ))}

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={addRow}
          disabled={links.length >= 10}
          data-testid="social-links-add"
          className="rounded-full"
        >
          <Plus size={13} className="mr-1" /> {t("social_links_add")}
        </Button>
        <Button
          type="button"
          onClick={save}
          disabled={busy || !canSave}
          data-testid="social-links-save"
          className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white hover:text-white"
        >
          {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : null}
          {t("social_links_save")}
        </Button>
      </div>
    </div>
  );
}
